import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import {
  getUserOrganizations,
  switchOrganization as switchOrganizationApi,
  UserOrganization,
} from '../services/organizationService';

interface OrganizationContextType {
  /** All organizations the user belongs to */
  organizations: UserOrganization[];
  /** The currently active organization */
  currentOrganization: UserOrganization | null;
  /** Whether we're loading organizations */
  isLoading: boolean;
  /** Whether user has multiple organizations (for showing switcher) */
  hasMultipleOrganizations: boolean;
  /** Switch to a different organization (triggers page reload) */
  switchOrganization: (organizationId: number) => Promise<void>;
  /** Refresh the organizations list */
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType>({
  organizations: [],
  currentOrganization: null,
  isLoading: true,
  hasMultipleOrganizations: false,
  switchOrganization: async () => {},
  refreshOrganizations: async () => {},
});

export const useOrganization = () => useContext(OrganizationContext);

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [organizations, setOrganizations] = useState<UserOrganization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<UserOrganization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Track whether we've already fetched for this user to prevent duplicate calls
  const hasFetchedRef = useRef(false);

  /**
   * Fetch user's organizations from the API
   */
  const refreshOrganizations = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setOrganizations([]);
      setCurrentOrganization(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await getUserOrganizations();

      setOrganizations(response.organizations);

      // Find the current organization
      const current = response.organizations.find(
        (org) => org.id === response.currentOrganizationId
      );
      setCurrentOrganization(current || null);
    } catch (error) {
      setOrganizations([]);
      setCurrentOrganization(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  /**
   * Switch to a different organization
   * After switching, performs a full page reload to refresh all data
   */
  const switchOrganization = useCallback(async (organizationId: number) => {
    try {
      await switchOrganizationApi(organizationId);
      // Full page reload to ensure all data is refreshed
      window.location.reload();
    } catch (error) {
      throw error;
    }
  }, []);

  // Load organizations once when user is authenticated
  // Uses user.id as stable dependency instead of user object reference
  const userId = user?.id;
  useEffect(() => {
    if (isAuthenticated && userId) {
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
        refreshOrganizations();
      }
    } else {
      hasFetchedRef.current = false;
      setOrganizations([]);
      setCurrentOrganization(null);
      setIsLoading(false);
    }
    // refreshOrganizations intentionally excluded — it changes reference when user changes,
    // but we only want to fetch once per userId, not on every user object reference change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userId]);

  const hasMultipleOrganizations = organizations.length > 1;

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        currentOrganization,
        isLoading,
        hasMultipleOrganizations,
        switchOrganization,
        refreshOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationContext;
